package com.bikompanion.data.gbfs

import dev.zacsweers.metro.Inject
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import com.bikompanion.domain.BikeNetwork
import com.bikompanion.domain.BikeNetworkRepository
import com.bikompanion.domain.BikeStation
import com.bikompanion.domain.Snapshot
import com.bikompanion.domain.catchingRequest
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

/** Anything the feed doesn't call human-powered counts as an e-bike on the markers. */
private const val PROPULSION_HUMAN = "human"

/**
 * The Barcelona GBFS feeds, mapped onto the same domain as CityBikes.
 *
 * One network per entry in [GbfsSystems]. Availability comes from two feeds that have to be
 * joined: `station_information` holds where a dock is and what it is called, `station_status`
 * holds how many bikes are in it right now.
 */
@OptIn(ExperimentalTime::class)
@Inject
class GbfsBikeNetworkRepository(
    private val api: GbfsApi,
) : BikeNetworkRepository {
    /**
     * Every system's description and service area.
     *
     * Reads `station_information` as well as `system_information`, because the coverage the
     * map filters and dims by can only be derived from where the docks actually are. Both are
     * static and cached — twelve hours in memory, and on disk between launches — so the cost
     * lands once, and it buys the ability to poll only the systems the camera can see.
     */
    override suspend fun fetchNetworks(): List<BikeNetwork> =
        coroutineScope {
            GbfsSystems
                .map { system -> async { loadNetwork(system) } }
                .awaitAll()
                // A single operator's feed being down must not take the other one off the map.
                .filterNotNull()
                .ifEmpty { error("No GBFS system could be reached") }
        }

    override suspend fun fetchStations(networkId: String): Snapshot<List<BikeStation>> {
        // A feed has nothing to say about any other network, and answering with Barcelona's
        // docks would put them on the map wherever the caller was actually looking.
        val system = GbfsSystems.firstOrNull { it.networkId == networkId }
            ?: return Snapshot(emptyList(), now(), isStale = false)

        val electricTypeIds = api
            .vehicleTypes(system)
            .data.vehicleTypes
            .filter { it.propulsionType != PROPULSION_HUMAN }
            .mapTo(mutableSetOf()) { it.vehicleTypeId }
        val information = api
            .stationInformation(system)
            .data.stations
            .associateBy { it.stationId }
        val status = api.stationStatus(system).data.stations

        val stations = status.mapNotNull { live ->
            // A dock that has been pulled out of the ground still appears in the feed, and a
            // status row can outlive its description; neither can be drawn.
            if (!live.isInstalled) return@mapNotNull null
            val place = information[live.stationId] ?: return@mapNotNull null
            BikeStation(
                id = live.stationId,
                name = place.name,
                networkId = system.networkId,
                latitude = place.lat,
                longitude = place.lon,
                freeBikes = live.vehiclesAvailable,
                emptySlots = live.docksAvailable,
                electricBikes = live.electricBikes(electricTypeIds),
                supportsElectricBikes = electricTypeIds.isNotEmpty(),
            )
        }
        return Snapshot(value = stations, updatedAtEpochMillis = now(), isStale = false)
    }

    private suspend fun loadNetwork(system: GbfsSystem): BikeNetwork? =
        catchingRequest {
            val info = api.systemInformation(system).data
            val coverage = coverageOf(api.stationInformation(system).data.stations)
            BikeNetwork(
                id = system.networkId,
                name = info.name.ifBlank { system.fallbackName },
                // The centre of the service area rather than anything the feed states: GBFS gives
                // a system no coordinates of its own, and this is the point the low-zoom marker is
                // drawn at, so it belongs in the middle of the docks it stands for.
                latitude = coverage?.bounds?.let { (it.south + it.north) / 2 } ?: 0.0,
                longitude = coverage?.bounds?.let { (it.west + it.east) / 2 } ?: 0.0,
                // GBFS carries no postal geography; both are constant for a single-city feed.
                city = system.city,
                country = system.country,
                company = info.operator
                    .takeIf { it.isNotBlank() }
                    ?.let { listOf(it) }
                    .orEmpty(),
                href = info.url.orEmpty().ifEmpty { system.discoveryUrl },
                licenseName = info.licenseId.orEmpty(),
                // Bicing publishes neither; AMBici declares CC0. `terms_url` is the closest thing
                // when a licence proper isn't given.
                licenseUrl = info.licenseUrl ?: info.termsUrl,
                coverage = coverage,
            )
        }.getOrNull()

    private fun now() = Clock.System.now().toEpochMilliseconds()
}

/**
 * How many of a station's bikes are electric, reconciled against its own headline count.
 *
 * The two numbers the feed publishes disagree: on roughly one Bicing station in fifty the
 * per-type breakdown doesn't sum to `num_vehicles_available`, and a handful of those claim
 * more e-bikes than bikes. Left alone that lands in the UI as a negative count of ordinary
 * bikes and a marker drawn with a segment of negative width.
 *
 * `num_vehicles_available` is the number GBFS treats as authoritative and the only one that
 * has to be right for "can I get a bike here", so it wins and the split is clamped to fit.
 */
private fun GbfsStationStatusDto.electricBikes(electricTypeIds: Set<String>): Int =
    vehicleTypesAvailable
        .filter { it.vehicleTypeId in electricTypeIds }
        .sumOf { it.count }
        // Ordered so a feed that ever reports a negative total still yields zero, not a throw.
        .coerceAtMost(vehiclesAvailable)
        .coerceAtLeast(0)
